import { useCallback } from "react";
import { PlaybookApiError } from "@playbookai/api-clients";
import type {
  DashboardPageRecord,
  DashboardRecord,
  WidgetKind,
  WidgetWidth,
} from "@playbookai/types";
import { dashboardApi } from "./api.js";
import {
  defaultPageOf,
  layoutToBatchItems,
  pageToLayout,
  type DashboardLayoutItem,
} from "./layout.js";
import type { UseDashboardResult } from "./use-dashboard.js";

/**
 * CAS-guarded dashboard mutations over the shared client (§12.6): expected
 * versions come from the freshest cached record, every success reconciles the
 * cache with the server's authoritative DashboardRecord, and a stale write
 * (409 DASHBOARD_VERSION_CONFLICT) NEVER silently overwrites — the cache is dropped,
 * authoritative configuration refetches, and the host tells the user their
 * change was not saved via `onConflict`. Every other failure (400/403/500)
 * reaches the host through `onError`, so a rejected write is never invisible.
 *
 * Every mutation RESOLVES with the reconciled record (or `undefined` when the
 * write failed) so a host that must issue two writes for one user edit — the
 * Personal Home connection PUT + instance PATCH — can await the first and let
 * the second read the versions the server just returned, instead of racing
 * itself into a self-inflicted 409.
 *
 * Layout changes (reorder/width) apply optimistically for pointer/keyboard
 * responsiveness, then commit as ONE widget-layout batch.
 */

export function isDashboardVersionConflict(error: unknown): boolean {
  return (
    error instanceof PlaybookApiError &&
    error.status === 409 &&
    error.envelope.code === "DASHBOARD_VERSION_CONFLICT"
  );
}

/**
 * The server's error code for a failed mutation (§11.7) — how a host maps
 * `DASHBOARD_PAGE_LIMIT_REACHED` / `DASHBOARD_LAST_PAGE_REQUIRED` / `DASHBOARD_DEFAULT_PAGE_REQUIRED` and
 * friends onto its own copy without re-deriving the envelope shape.
 */
export function dashboardErrorCode(error: unknown): string | undefined {
  return error instanceof PlaybookApiError ? error.envelope.code : undefined;
}

/** Resolved record, or `undefined` when the write failed (already reported). */
export type DashboardMutationResult = Promise<DashboardRecord | undefined>;

export interface UseDashboardMutationsArgs {
  readonly dashboard: UseDashboardResult;
  /**
   * The page widget mutations target. Defaults to the dashboard's default
   * page — correct for single-page Personal Home, wrong for a multi-page host
   * that must write to the page the editor is looking at.
   */
  readonly activePageId?: string | undefined;
  /** Called after a lost CAS race (the change was not saved). */
  readonly onConflict?: (error: PlaybookApiError) => void;
  /** Called for non-conflict mutation failures. */
  readonly onError?: (error: unknown) => void;
}

export interface DashboardMutations {
  readonly onReorder: (
    next: readonly DashboardLayoutItem[],
  ) => DashboardMutationResult;
  readonly onChangeWidth: (
    instanceId: string,
    width: WidgetWidth,
  ) => DashboardMutationResult;
  readonly onAddWidget: (kind: WidgetKind) => DashboardMutationResult;
  readonly onRemoveWidget: (instanceId: string) => DashboardMutationResult;
  readonly onEditWidget: (
    instanceId: string,
    next: {
      params?: Record<string, unknown>;
      /** `null` clears the override; omitted/undefined leaves it untouched. */
      title?: string | null | undefined;
    },
  ) => DashboardMutationResult;
  readonly setConnection: (
    instanceId: string,
    slotKey: string,
    selection: Record<string, unknown>,
  ) => DashboardMutationResult;
  /** Clear a slot's saved binding — the only way to un-pick a KPI (§11.4). */
  readonly removeConnection: (
    instanceId: string,
    slotKey: string,
  ) => DashboardMutationResult;
  readonly createPage: (title: string) => DashboardMutationResult;
  readonly renamePage: (
    pageId: string,
    title: string,
  ) => DashboardMutationResult;
  readonly reorderPages: (
    pageIds: readonly string[],
  ) => DashboardMutationResult;
  readonly deletePage: (pageId: string) => DashboardMutationResult;
}

export function useDashboardMutations(
  args: UseDashboardMutationsArgs,
): DashboardMutations {
  const { dashboard, activePageId, onConflict, onError } = args;
  const { config, reconcile, invalidate, peek, cancel } = dashboard;

  const settle = useCallback(
    (persist: () => Promise<DashboardRecord>): DashboardMutationResult =>
      persist()
        .then((next) => {
          reconcile(next);
          return next;
        })
        .catch((error: unknown) => {
          invalidate();
          if (isDashboardVersionConflict(error)) {
            onConflict?.(error as PlaybookApiError);
          } else {
            onError?.(error);
          }
          return undefined;
        }),
    [reconcile, invalidate, onConflict, onError],
  );

  /** Freshest record — mutations must read versions at call time, not render time. */
  const current = useCallback(
    (): DashboardRecord | undefined => peek() ?? dashboard.query.data,
    [peek, dashboard.query.data],
  );

  /** The page widget mutations write to (the active page, else the default). */
  const targetPage = useCallback(
    (record: DashboardRecord): DashboardPageRecord | undefined =>
      record.pages.find((page) => page.id === activePageId) ??
      defaultPageOf(record),
    [activePageId],
  );

  const pageById = useCallback(
    (
      record: DashboardRecord,
      pageId: string,
    ): DashboardPageRecord | undefined =>
      record.pages.find((page) => page.id === pageId),
    [],
  );

  const commitLayout = useCallback(
    async (items: readonly DashboardLayoutItem[]): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const page = targetPage(record);
      if (!page) return undefined;
      await cancel();
      // Optimistic: rewrite the page's order/width in the cached record.
      const byId = new Map(
        items.map((item, order) => [
          item.instanceId,
          { order, width: item.width },
        ]),
      );
      reconcile({
        ...record,
        pages: record.pages.map((p) =>
          p.id === page.id
            ? {
                ...p,
                widgetInstances: p.widgetInstances.map((instance) => {
                  const target = byId.get(instance.id);
                  return target
                    ? {
                        ...instance,
                        displayOrder: target.order,
                        width: target.width,
                      }
                    : instance;
                }),
              }
            : p,
        ),
      });
      return settle(() =>
        dashboardApi.updateWidgetLayout(config, record.id, page.id, {
          expectedDashboardVersion: record.version,
          expectedPageVersion: page.version,
          items: layoutToBatchItems(items),
        }),
      );
    },
    [cancel, config, current, reconcile, settle, targetPage],
  );

  const onReorder = useCallback(
    (next: readonly DashboardLayoutItem[]): DashboardMutationResult =>
      commitLayout(next),
    [commitLayout],
  );

  const onChangeWidth = useCallback(
    async (instanceId: string, width: WidgetWidth): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const page = targetPage(record);
      if (!page) return undefined;
      const items = pageToLayout(page).map((item) =>
        item.instanceId === instanceId ? { ...item, width } : item,
      );
      return commitLayout(items);
    },
    [commitLayout, current, targetPage],
  );

  const onAddWidget = useCallback(
    async (kind: WidgetKind): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const page = targetPage(record);
      if (!page) return undefined;
      return settle(() =>
        dashboardApi.createWidgetInstance(config, record.id, page.id, {
          expectedDashboardVersion: record.version,
          expectedPageVersion: page.version,
          widgetKind: kind,
        }),
      );
    },
    [config, current, settle, targetPage],
  );

  const onRemoveWidget = useCallback(
    async (instanceId: string): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const page = record.pages.find((p) =>
        p.widgetInstances.some((i) => i.id === instanceId),
      );
      if (!page) return undefined;
      return settle(() =>
        dashboardApi.deleteWidgetInstance(config, record.id, instanceId, {
          expectedDashboardVersion: record.version,
          expectedPageVersion: page.version,
        }),
      );
    },
    [config, current, settle],
  );

  const onEditWidget = useCallback(
    async (
      instanceId: string,
      next: {
        params?: Record<string, unknown>;
        /** `null` clears the override; omitted/undefined leaves it untouched. */
        title?: string | null | undefined;
      },
    ): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const instance = record.pages
        .flatMap((p) => p.widgetInstances)
        .find((i) => i.id === instanceId);
      if (!instance) return undefined;
      return settle(() =>
        dashboardApi.updateWidgetInstance(config, record.id, instanceId, {
          expectedDashboardVersion: record.version,
          expectedWidgetVersion: instance.version,
          // `null` clears the override; `undefined` leaves it untouched.
          ...(next.title !== undefined ? { titleOverride: next.title } : {}),
          ...(next.params !== undefined ? { params: next.params } : {}),
        }),
      );
    },
    [config, current, settle],
  );

  const setConnection = useCallback(
    async (
      instanceId: string,
      slotKey: string,
      selection: Record<string, unknown>,
    ): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const instance = record.pages
        .flatMap((p) => p.widgetInstances)
        .find((i) => i.id === instanceId);
      if (!instance) return undefined;
      const connection = instance.connections.find(
        (c) => c.slotKey === slotKey,
      );
      return settle(() =>
        dashboardApi.putConnection(config, record.id, instanceId, slotKey, {
          expectedDashboardVersion: record.version,
          ...(connection !== undefined
            ? { expectedConnectionVersion: connection.version }
            : {}),
          selection,
        }),
      );
    },
    [config, current, settle],
  );

  const removeConnection = useCallback(
    async (instanceId: string, slotKey: string): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const connection = record.pages
        .flatMap((p) => p.widgetInstances)
        .find((i) => i.id === instanceId)
        ?.connections.find((c) => c.slotKey === slotKey);
      // Nothing saved for the slot — there is no binding to clear.
      if (!connection) return undefined;
      return settle(() =>
        dashboardApi.deleteConnection(config, record.id, instanceId, slotKey, {
          expectedDashboardVersion: record.version,
          expectedConnectionVersion: connection.version,
        }),
      );
    },
    [config, current, settle],
  );

  const createPage = useCallback(
    async (title: string): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      return settle(() =>
        dashboardApi.createPage(config, record.id, {
          expectedDashboardVersion: record.version,
          title,
        }),
      );
    },
    [config, current, settle],
  );

  const renamePage = useCallback(
    async (pageId: string, title: string): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const page = pageById(record, pageId);
      if (!page) return undefined;
      return settle(() =>
        dashboardApi.updatePage(config, record.id, pageId, {
          expectedDashboardVersion: record.version,
          expectedPageVersion: page.version,
          title,
        }),
      );
    },
    [config, current, pageById, settle],
  );

  const reorderPages = useCallback(
    async (pageIds: readonly string[]): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      return settle(() =>
        dashboardApi.reorderPages(config, record.id, {
          expectedDashboardVersion: record.version,
          pageIds,
        }),
      );
    },
    [config, current, settle],
  );

  const deletePage = useCallback(
    async (pageId: string): DashboardMutationResult => {
      const record = current();
      if (!record) return undefined;
      const page = pageById(record, pageId);
      if (!page) return undefined;
      return settle(() =>
        dashboardApi.deletePage(config, record.id, pageId, {
          expectedDashboardVersion: record.version,
          expectedPageVersion: page.version,
        }),
      );
    },
    [config, current, pageById, settle],
  );

  return {
    onReorder,
    onChangeWidth,
    onAddWidget,
    onRemoveWidget,
    onEditWidget,
    setConnection,
    removeConnection,
    createPage,
    renamePage,
    reorderPages,
    deletePage,
  };
}
